import type { JSX } from 'react';
import { useDeferredValue, useMemo, useState } from 'react';

import { ChevronDown, Search } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

import SubProjectConfigCard from './SubProjectConfigCard';

interface SubProjectOption {
  id: number;
  name: string;
}

interface PanelUser {
  id: number;
  name: string | null;
  email: string;
}

interface SubProjectScoreConfig {
  minScore: number;
  considerAssignment: boolean;
}

interface SubProjectAdvancedConfigProps {
  subProjects: SubProjectOption[];
  byProjects: Record<string, SubProjectScoreConfig>;
  byProjectUser: Record<string, number[]>;
  sortedUsers: PanelUser[];
  selectedUserIdSet: Set<number>;
  globalMinScore: number;
  globalConsiderAssignment: boolean;
  onUpdateScoreConfig: (subProjectId: number, next: { minScore?: number; considerAssignment?: boolean }) => void;
  onRemoveScoreConfig: (subProjectId: number) => void;
  onEditRecipients: (subProjectId: number) => void;
  onRemoveRecipients: (subProjectId: number) => void;
}

const normalizeSearch = (value: string): string => value.trim().toLowerCase();

const SubProjectAdvancedConfig = ({
  subProjects,
  byProjects,
  byProjectUser,
  sortedUsers,
  selectedUserIdSet,
  globalMinScore,
  globalConsiderAssignment,
  onUpdateScoreConfig,
  onRemoveScoreConfig,
  onEditRecipients,
  onRemoveRecipients,
}: SubProjectAdvancedConfigProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showOnlyCustom, setShowOnlyCustom] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const deferredSearch = useDeferredValue(search);
  const normalizedSearch = normalizeSearch(deferredSearch);

  const isCustom = useMemo(
    () =>
      (subProjectId: number): boolean => {
        const key = String(subProjectId);
        return key in byProjects || key in byProjectUser;
      },
    [byProjects, byProjectUser]
  );

  const customCount = useMemo(
    () => subProjects.filter((subProject) => isCustom(subProject.id)).length,
    [subProjects, isCustom]
  );

  const filteredSubProjects = useMemo(
    () =>
      subProjects.filter((subProject) => {
        if (showOnlyCustom && !isCustom(subProject.id)) {
          return false;
        }
        if (normalizedSearch && !subProject.name.toLowerCase().includes(normalizedSearch)) {
          return false;
        }
        return true;
      }),
    [subProjects, showOnlyCustom, isCustom, normalizedSearch]
  );

  const hasSearch = normalizedSearch.length > 0;
  const isFiltered = hasSearch || showOnlyCustom;

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="rounded-lg border border-border-subtle bg-bg-surface shadow-xs"
    >
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
        <span className="flex items-center gap-2">
          <span className="text-sm font-semibold text-brand-navy font-display">
            Configuración avanzada por subproyecto
          </span>
          {customCount > 0 ? (
            <Badge variant="secondary">
              {customCount} {customCount === 1 ? 'personalizado' : 'personalizados'}
            </Badge>
          ) : null}
        </span>
        <ChevronDown
          className={cn('size-4 shrink-0 text-text-muted transition-transform', isOpen ? 'rotate-0' : '-rotate-90')}
          aria-hidden
        />
      </CollapsibleTrigger>

      <CollapsibleContent className="border-t border-border-subtle">
        <div className="border-b border-border-subtle bg-bg-surface px-4 py-3">
          <div className="space-y-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-muted" />
              <Input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar subproyecto…"
                className="h-9 pl-9 text-sm"
              />
            </div>
            <div className="flex items-center justify-between gap-3 text-xs text-text-muted font-body">
              <label htmlFor="show-only-custom-subprojects" className="flex cursor-pointer items-center gap-2">
                <input
                  id="show-only-custom-subprojects"
                  type="checkbox"
                  className="size-3.5 shrink-0 rounded border-input accent-brand-vivid"
                  checked={showOnlyCustom}
                  onChange={(event) => setShowOnlyCustom(event.target.checked)}
                />
                Solo personalizados
              </label>
              <span>
                {isFiltered
                  ? `Mostrando ${filteredSubProjects.length} de ${subProjects.length}`
                  : `${subProjects.length} subproyectos`}
              </span>
            </div>
          </div>
        </div>

        {filteredSubProjects.length === 0 ? (
          <p className="m-0 px-4 py-6 text-sm text-text-subtle font-body">
            No hay subproyectos que coincidan con el filtro.
          </p>
        ) : (
          <ScrollArea className="max-h-[26rem]">
            <div className="flex flex-col gap-3 p-4">
              {filteredSubProjects.map((subProject) => {
                const key = String(subProject.id);
                const subConfig = byProjects[key] ?? null;
                const userIds = byProjectUser[key] ?? [];
                const hasCustomRecipients = key in byProjectUser;
                const previewUsers = sortedUsers
                  .filter((user) => userIds.includes(user.id))
                  .slice(0, 3)
                  .map((user) => ({ id: user.id, name: user.name || user.email }));
                const hiddenUserCount = Math.max(userIds.length - previewUsers.length, 0);
                const overlapCount = userIds.filter((id) => selectedUserIdSet.has(id)).length;

                return (
                  <SubProjectConfigCard
                    key={subProject.id}
                    subProjectId={subProject.id}
                    name={subProject.name}
                    subConfig={subConfig}
                    recipientCount={userIds.length}
                    hasCustomRecipients={hasCustomRecipients}
                    previewUsers={previewUsers}
                    hiddenUserCount={hiddenUserCount}
                    overlapCount={overlapCount}
                    globalMinScore={globalMinScore}
                    globalConsiderAssignment={globalConsiderAssignment}
                    isExpanded={expandedId === subProject.id}
                    onToggleExpand={(id) => setExpandedId((prev) => (prev === id ? null : id))}
                    onUpdateScoreConfig={onUpdateScoreConfig}
                    onRemoveScoreConfig={onRemoveScoreConfig}
                    onEditRecipients={onEditRecipients}
                    onRemoveRecipients={onRemoveRecipients}
                  />
                );
              })}
            </div>
          </ScrollArea>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};

export default SubProjectAdvancedConfig;
